import {
    AfterContentInit,
    Component,
    ContentChild, ContentChildren,
    EventEmitter,
    Input,
    Output, QueryList,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { ControlBase } from "@mipro/custom-controls";
import { EnabledModule } from "./enabled-module";
import { FormButtonsConfig } from "@mipro/components";
import { ModuleType } from "./company-details.component";
import { UserHelperService } from "@main/users/user-helper.service";
import { ConfirmationService, SelectItem } from "primeng/api";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { NamingConventionService } from "@mipro/services/naming-convention.service";

@Component({
    selector: "enabled-module",
    template: `
        <div class="inputs-group-row">
            <label class="inputs-group-row-name vertical-align-baseline">{{ enabledModule.moduleName }}:</label>
            <div class="inputs-group-row-value">
                <div class="flex">
                    <div>
                        <p-checkbox [binary]="true" [(ngModel)]="enabled" [disabled]="disabled" (onChange)="onCheckboxModuleChanged(enabledModule.moduleName, $event)"> </p-checkbox>
                        <a *ngIf="extraInfo" class="ml-4"> {{ extraInfo }} </a>
                    </div>
                    <button
                        *ngIf="canDeleteFormModule"
                        style="margin-left: 20px"
                        type="button"
                        (click)="deleteWithConfirmation()"
                        class="p-panel-header-icon p-link mr-2">
                        <span class="mi mi-button-remove text-danger"></span>
                    </button>
                </div>
            </div>
            <div class="inputs-group-row-value"></div>
            <div *ngIf="enabled" class="flex mt-1">
                <p-floatLabel style="width: 100%;">
                    <p-dropdown
                        [(ngModel)]="dashboardId"
                        [id]="enabledModule.moduleName"
                        placeholder="Select a dashboard"
                        [options]="dashboards"
                        [showClear]="true"
                        appendTo="body" />
                    <label [for]="enabledModule.moduleName">Module Dashboard</label>
                </p-floatLabel>
            </div>
        </div>
        <ng-container #content> </ng-container>
    `,
    styleUrls: ["./enabled-module-control.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: EnabledModuleControlComponent,
            multi: true
        }
    ]
})
export class EnabledModuleControlComponent extends ControlBase<EnabledModule> implements AfterContentInit {
    protected enabledModule: EnabledModule = {} as any;
    deleteModuleDialogVisible: boolean = false;

    buttonsConfig = <FormButtonsConfig>{
        saveLabel: "Yes",
        saveFunc: () => {
            this.onDeleteModule.emit(this.enabledModule.moduleName);
            this.deleteModuleDialogVisible = false;
        },
        justifyContent: "end"
    };

    @Input() extraInfo: string = null;
    @Input() organizationId: string = null;

    @Output() readonly enabledChange = new EventEmitter<EnabledModule>();
    @Output() readonly onDeleteModule = new EventEmitter<string>();
    @Output() readonly disableNhsAccepted = new EventEmitter();

    @ViewChild("content", { read: ViewContainerRef, static: true }) private contentContainer: ViewContainerRef;
    @ContentChildren(TemplateRef) private contentTemplates: QueryList<TemplateRef<any>>;

    dashboards: SelectItem[];

    protected get enabled() {
        return this.enabledModule.enabled;
    }

    protected set enabled(enabled: boolean) {
        this.enabledModule.enabled = enabled;
        this.onTouched();
        this.onChange(this.enabledModule);
        this.enabledChange.emit(this.enabledModule);
        this.updateContent();
    }

    protected get dashboardId() {
        return this.enabledModule.defaultDashboardId;
    }

    protected set dashboardId(dashboardId: string) {
        this.enabledModule.defaultDashboardId = dashboardId;
        this.onTouched();
        this.onChange(this.enabledModule);
        this.enabledChange.emit(this.enabledModule);
        this.updateContent();
    }

    get canDeleteFormModule() {
        return this.enabledModule.type == ModuleType.Formio && this.userHelperService.isTopAdmin;
    }

    constructor(private userHelperService: UserHelperService,
        private confirmationService: ConfirmationService,
        private namingConventionService: NamingConventionService) {
        super();
    }

    writeValue(obj: EnabledModule): void {
        this.enabledModule = obj;
        this.dashboards = this.enabledModule.dashboards.map(d => <SelectItem>{ label: d.name, value: d.id });
        this.updateContent();
    }

    ngAfterContentInit(): void {
        this.updateContent();
    }

    onCheckboxModuleChanged(moduleName: string, event: any){
        if(moduleName == MiProModules.NHS && !event.checked) {
            this.confirmationService.confirm({
                message: `Are you sure you want to disable the NHS module for the current ${this.namingConventionService.organisationSingular}?\n
                 Please note that all associated NHS data will be permanently removed.`,
                accept: () => {
                    this.enabledModule.enabled = false;
                    this.disableNhsAccepted.emit();
                },
                reject: () => {
                    this.enabled = true;
                },
            });
        }
    }

    private updateContent() {
        if (!this.contentTemplates || !this.enabledModule?.enabled) {
            this.contentContainer.clear();
            return;
        }

        this.contentContainer.clear();
        this.contentTemplates.forEach(template => {
            this.contentContainer.createEmbeddedView(template);
        });
    }

    protected deleteWithConfirmation() {
        this.confirmationService.confirm({
            message: `This module will be deleted for all ${this.namingConventionService.organisationPlural}.\n
            Are you sure you want to remove this Module?`,
            accept: () => {
                this.onDeleteModule.emit(this.enabledModule.moduleName);
            }
        });
    }
}
